import React, { useEffect, useState } from "react";
import { ScreenHeader } from "@/components/ScreenHeader";
import { api } from "@/api/client";
import { colors, spacing, fonts } from "@/theme";

function LocationPicker({ selected, onSelect, options, testID }) {
  return (
    <div data-testid={testID} style={pk.row}>
      {options.map((opt) => {
        const active = selected === opt;
        return (
          <button key={opt} type="button" data-testid={`${testID}-${opt}`} onClick={() => onSelect(opt)}
            style={{ ...pk.chip, ...(active ? pk.chipActive : {}) }}>
            <span style={{ ...pk.text, color: active ? colors.onBrandPrimary : colors.onSurface }}>{opt}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function Create() {
  const [trailer, setTrailer] = useState("");
  const [yard, setYard] = useState("");
  const [fromLoc, setFromLoc] = useState("");
  const [toLoc, setToLoc] = useState("");
  const [notes, setNotes] = useState("");
  const [locations, setLocations] = useState([]);
  const [yards, setYards] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api("/locations");
        setLocations(r.locations);
        setYards(r.yards || []);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!trailer || !yard || !fromLoc || !toLoc) { setMsg({ type: "err", text: "All fields required" }); return; }
    setBusy(true); setMsg(null);
    try {
      await api("/moves", { body: { trailer_number: trailer, yard, from_location: fromLoc, to_location: toLoc, notes: notes || undefined } });
      setMsg({ type: "ok", text: `Move created · ${trailer} · ${yard}` });
      setTrailer(""); setYard(""); setFromLoc(""); setToLoc(""); setNotes("");
    } catch (e2) {
      setMsg({ type: "err", text: e2?.message || "Failed" });
    } finally { setBusy(false); }
  };

  return (
    <div style={{ flex: 1, backgroundColor: colors.surface }}>
      <ScreenHeader title="New Move" subtitle="Dispatch a trailer" />
      <form style={s.body} onSubmit={submit}>
        <div style={s.label}>Trailer number</div>
        <input data-testid="input-trailer" style={s.input} value={trailer}
          onChange={(e) => setTrailer(e.target.value.toUpperCase())} placeholder="TRL-1234" />

        <div style={s.label}>Yard</div>
        <LocationPicker testID="picker-yard" selected={yard} onSelect={setYard} options={yards} />

        <div style={s.label}>From location</div>
        <LocationPicker testID="picker-from" selected={fromLoc} onSelect={setFromLoc} options={locations} />

        <div style={s.label}>To location</div>
        <LocationPicker testID="picker-to" selected={toLoc} onSelect={setToLoc} options={locations} />

        <div style={s.label}>Notes (optional)</div>
        <textarea data-testid="input-notes" style={{ ...s.input, height: 80, resize: "vertical" }} value={notes}
          onChange={(e) => setNotes(e.target.value)} placeholder="e.g. priority load" />

        {msg && (
          <div style={{ ...s.msg, ...(msg.type === "ok" ? s.msgOk : s.msgErr) }}>
            <span style={s.msgText}>{msg.text}</span>
          </div>
        )}

        <button type="submit" data-testid="submit-move" disabled={busy} style={{ ...s.cta, ...(busy ? { opacity: 0.6 } : {}) }}>
          <span style={s.ctaText}>{busy ? "…" : "Create move →"}</span>
        </button>
      </form>
    </div>
  );
}

const pk = {
  row: { display: "flex", flexWrap: "wrap", gap: 8, padding: "8px 0" },
  chip: { padding: "10px 14px", borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.surfaceSecondary, cursor: "pointer" },
  chipActive: { backgroundColor: colors.brandPrimary, borderColor: colors.brandPrimary },
  text: { ...fonts.monoBold, fontSize: 12, letterSpacing: 0 },
};

const s = {
  body: { padding: spacing.xl, maxWidth: 640, display: "flex", flexDirection: "column" },
  label: { ...fonts.monoBold, fontSize: 11, color: colors.onSurfaceTertiary, marginTop: spacing.lg, marginBottom: spacing.xs },
  input: { width: "100%", borderWidth: 2, borderStyle: "solid", borderColor: colors.onSurface, backgroundColor: colors.surfaceSecondary, padding: spacing.md, ...fonts.mono, fontSize: 16, color: colors.onSurface, outline: "none", boxSizing: "border-box" },
  cta: { marginTop: spacing.xl, backgroundColor: colors.brandPrimary, padding: 18, borderWidth: 2, borderStyle: "solid", borderColor: colors.brandPrimary, cursor: "pointer" },
  ctaText: { color: colors.onBrandPrimary, ...fonts.display, fontSize: 16 },
  msg: { marginTop: spacing.md, padding: spacing.md, borderWidth: 2, borderStyle: "solid" },
  msgOk: { backgroundColor: colors.onSuccess, borderColor: colors.success },
  msgErr: { backgroundColor: colors.onError, borderColor: colors.error },
  msgText: { ...fonts.monoBold, fontSize: 12, color: colors.onSurface },
};
